import { WpModes } from "../../../constants";
import classnames from "classnames";
import { Utils } from "helpers/utils";
import { useRef, useState } from "react";
import { PostCommands, WpSliceData, WpStatus } from "../../../redux/workplace/wpTypes";
import { setActivePostId2, setFlag, changePostStatus, setUnread, setPostInfoOnSignalR, setCreateSubTask } from "../../../redux/workplace/wpActions";
import { changeSelectedChat } from "../../../redux/actions";
import { APIClient } from "../../../api/apiCore";
import * as urls from "../../../api/urls"
import { useRedux } from "hooks";
import { createSelector } from "reselect";
import { Tooltip } from "react-tooltip";
import { useDialog } from "DialogProvider";
import Loader from "components/Loader";
import { LoadingIcon } from "yet-another-react-lightbox";
import LoadingIconWithText from "components/LoadingIconWithText";

interface PostCommandProps {
  showAsDropdown: boolean;
  wpMode: WpModes;
  onCommandClick: (sender: any, cmd: PostCommands, postId: string) => void;
}

const PostCommand = ({ showAsDropdown, wpMode, onCommandClick }: PostCommandProps) => {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.WpSlice,
    (state: any) => state.Layout,
    (wp: WpSliceData, layout) => ({
      activePost: wp.activePostInfo,
      isEmbChatOnly: wp.isEmbChatOnly,
      viewPortSize: layout.viewPortSize,
      isInFrame: layout.isInFrame,
    })
  );
  const { activePost, viewPortSize, isInFrame, isEmbChatOnly } = useAppSelector(storeData);
  const deviceOs = Utils.GetDeviceOS();
  const isWindows = deviceOs === "Windows";

  const commands = [
    PostCommands.ConvertToTask,
    PostCommands.MarkAsUnread,
    PostCommands.SharePost,
    PostCommands.Snooze,
    PostCommands.Flag,
    PostCommands.PostStatus,
    PostCommands.CreateSubTask,
    PostCommands.GoToMainTask,
    PostCommands.RecurranceSetting,
    PostCommands.PostInfo,
    PostCommands.DeletePost,
  ];

  const [isLoading, setIsLoading] = useState<PostCommands[]>([]);

  const setIsLoadingEx = (cmd: PostCommands, loading: boolean) => {
    setIsLoading(prev => {
      if (loading) {
        if (prev.includes(cmd)) return prev;
        return [...prev, cmd];
      } else {
        return prev.filter(c => c !== cmd);
      }
    });
  }

  const subTaskCount = (activePost.subTaskCount ?? 0) > 0 ? ` (${activePost.subTaskCount})` : "";

  const cmdTooltips: Record<PostCommands, string> = {
    [PostCommands.CreateSubTask]: 'สร้างงานย่อย' + subTaskCount,
    [PostCommands.GoToMainTask]: 'ไป งานหลัก',
    [PostCommands.ConvertToTask]: 'เปลี่ยนเป็น Task',
    [PostCommands.MarkAsUnread]: 'มาร์คว่ายังไม่อ่าน',
    [PostCommands.SharePost]: isWindows == true ? 'คัดลอกลิ้งค์' : 'แชร์โพส',
    [PostCommands.Snooze]: 'ตั้งเตือน',
    [PostCommands.Flag]: 'ติดธง',
    [PostCommands.PostStatus]: activePost.status == WpStatus.Open ? 'ปิดโพส' : 'เปิดโพสใหม่', // 'เปลี่ยนสถานะโพส',
    [PostCommands.PostInfo]: wpMode == WpModes.Workplace ? 'ข้อมูลโพส' : 'ข้อมูลงาน',
    [PostCommands.DeletePost]: wpMode == WpModes.Workplace ? 'ลบโพส' : 'ลบงาน',
    [PostCommands.RecurranceSetting]: "ตั้งค่างานประจำ",
  };

  const cmdIcons: Record<PostCommands, string> = {
    [PostCommands.CreateSubTask]: 'fas fa-file-export', //'far fa-calendar-plus',
    [PostCommands.GoToMainTask]: 'fab fa-mizuni', //'fas fa-location-arrow', 
    [PostCommands.ConvertToTask]: 'fas fa-hammer',
    [PostCommands.MarkAsUnread]: 'fas fa-book-open',
    [PostCommands.SharePost]: 'fa fa-share-alt',
    [PostCommands.Snooze]: 'material-icons fz-13|snooze',
    [PostCommands.Flag]: activePost.flag ? 'fas fa-flag' : 'far fa-flag',
    [PostCommands.PostStatus]: activePost.status == WpStatus.Open ? 'far fa-check-circle' : 'fas fa-check-circle',
    [PostCommands.PostInfo]: 'fas fa-info-circle',
    [PostCommands.DeletePost]: 'far fa-trash-alt',
    [PostCommands.RecurranceSetting]: 'fab fa-digital-ocean',
  };

  const isCommandActive = (cmd: PostCommands) => {
    if (cmd == PostCommands.Snooze && activePost.snooze) return true;
    if (cmd == PostCommands.Flag && activePost.flag) return true;
    if (cmd == PostCommands.PostStatus && activePost.status !== WpStatus.Open) return true;
    if (cmd == PostCommands.DeletePost) return true;
    return false;
  }

  const getTooltipId = (cmd: PostCommands, returnHint: boolean) => {
    if (cmd == PostCommands.SharePost) return returnHint ? "คัดลอกแล้ว" : "tipPostShare";
    return "";
  }

  const isCommandVisible = (cmd: PostCommands) => {
    if (cmd == PostCommands.DeletePost && activePost.canDeletePost == false) return false;
    if (cmd == PostCommands.PostStatus && wpMode == WpModes.Task) return false;
    if (cmd == PostCommands.ConvertToTask && wpMode !== WpModes.Workplace) return false;
    //if (cmd == PostCommands.RecurranceSetting && wpMode !== WpModes.Task) return false;
    if (cmd == PostCommands.RecurranceSetting) return false; // change to always hide from post menu
    if (cmd == PostCommands.CreateSubTask && (wpMode !== WpModes.Task || activePost.isSubTask == true)) return false;
    if (cmd == PostCommands.GoToMainTask && (wpMode !== WpModes.Task || activePost.isSubTask == false)) return false;
    return true;
  }

  const showInLine = !showAsDropdown;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const onMarkUnread = (cmd: PostCommands) => {
    try {
      setIsLoadingEx(cmd, true);
      const postId = activePost.postId ?? "";
      const api = new APIClient();
      api.fnPostForm(urls.WP_MARK_UNREAD, { postIdParent: postId })
        .then(rs => {
          setIsLoadingEx(cmd, false);
          dispatch(setActivePostId2(WpModes.Workplace, null, null, null, null, null, "Mark as unread"));
          dispatch(changeSelectedChat(null));
          dispatch(setUnread(wpMode, postId, 1));
        })
        .catch(e2 => {
          setIsLoadingEx(cmd, false);
          Utils.HandleException(e2, true);
        });
    } catch (e) {
      setIsLoadingEx(cmd, false);
      Utils.HandleException(e, true);
    }
  }

  const onGoToMainTask = async () => {
    setIsLoadingEx(PostCommands.GoToMainTask, true);
    await Utils.Tk_OpenTask(activePost.postId ?? "", true, dispatch);
    setIsLoadingEx(PostCommands.GoToMainTask, false);
  }

  const [tipPostShareShow, setTipPostShareShow] = useState<boolean>(false);

  const onSharePost = (cmd: PostCommands) => {
    try {
      const postId = activePost.postId ?? "";
      const baseUrl = Utils.GetBaseUrl();
      const queryString = wpMode == WpModes.Workplace ? "wpc" : "tkc";
      const URL = `${baseUrl}/redirect?${queryString}=${postId}`;
      navigator.clipboard.writeText(URL); // always copy to clipboard
      if (isWindows) {
        // navigator.clipboard.writeText(URL);
        setTipPostShareShow(true);
        setTimeout(() => { setTipPostShareShow(false); }, 1000);
      } else {
        if (navigator.canShare && navigator.canShare({ url: URL })) {
          navigator.share({
            url: URL,
            title: "Link หัวข้อโพส",
            text: "",
            //files: [file], // Share the actual image file
          });
        } else {
          // navigator.clipboard.writeText(URL);
          setTipPostShareShow(true);
          setTimeout(() => { setTipPostShareShow(false); }, 1000);
        }
      }

    } catch (e) {

    }
  }

  const onToggleFlag = async (cmd: PostCommands) => {
    try {
      setIsLoadingEx(cmd, true);
      const postId = activePost.postId ?? "";
      if (postId != "") {
        const api = new APIClient();
        api.fnPostForm(urls.WP_TOGGLE_FLAG, { postId: postId })
          .then(rs => {
            dispatch(setFlag(wpMode, postId, rs.flag));
            setIsLoadingEx(cmd, false);
          })
          .catch(e2 => {
            setIsLoadingEx(cmd, false);
            Utils.HandleException(e2, true);
          });
      }
    } catch (e) {
      setIsLoadingEx(cmd, false);
      Utils.HandleException(e, true);
    }
  }

  const onChangePostStatus = async (cmd: PostCommands) => {
    try {
      const postId = activePost.postId ?? "";
      const activePostStatus = activePost.status;
      const changeTo: WpStatus = activePostStatus === WpStatus.Open ? WpStatus.Close : WpStatus.Open;
      if (postId) {
        setIsLoadingEx(cmd, true);
        const changeToText = activePostStatus === WpStatus.Open ? Utils.GetWpStatus(WpStatus.Close) : Utils.GetWpStatus(WpStatus.Open);
        const confirm = `<h3>ยืนยันเปลี่ยนสถานะโพสเป็น <span class="text-danger">${changeToText}</span> ใช่หรือไม่</h3>`;
        Utils.SweetConfirm(confirm)
          .then(rs => {
            if (rs.isConfirmed) {
              const api = new APIClient();
              const parm = {
                fromDeviceId: Utils.GetDeviceId(),
                postId: postId,
                changeTo: changeTo
              };
              // console.log('change post status:', parm);
              api.fnPostForm(urls.WP_CHANG_POST_STATUS, parm)
                .then(rs2 => {
                  //dispatch(changePostStatus(postId, changeTo)); -> notify via signal R already
                  setIsLoadingEx(cmd, false);
                })
                .catch(e2 => {
                  setIsLoadingEx(cmd, false);
                  Utils.HandleException(e2, true);
                });
            } else {
              setIsLoadingEx(cmd, false);
            }
          })
          .catch(e2 => {
            setIsLoadingEx(cmd, false);
            Utils.HandleException(e2, true);
          });
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }

  }


  const onDeletePost = (cmd: PostCommands) => {
    try {
      const postId = activePost.postId ?? "";
      Utils.SweetConfirm('คุณต้องการลบโพสที่เลือก เป็นการถาวร ใช่หรือไม่?')
        .then(rs => {
          if (rs.isConfirmed) {
            setIsLoadingEx(cmd, true);
            const api = new APIClient();
            api.fnPostForm(urls.WP_DELETE_POST, { fromDeviceId: Utils.GetDeviceId(), postId: postId })
              .then(rs => {
                setIsLoadingEx(cmd, false);
                // console.log('rs:', rs);
              })
              .catch(e2 => {
                setIsLoadingEx(cmd, false);
                Utils.HandleException(e2, true);
              });
          }
        })
        .catch(e2 => Utils.HandleException(e2, true));
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }

  const dialog = useDialog();
  const onClickPostInfo = async (cmd: PostCommands) => {
    try {
      setIsLoadingEx(cmd, true);
      dialog.showPostInfo({ postId: activePost.postId ?? "" })
        .then(rs => setIsLoadingEx(cmd, false))
        .catch(ex => setIsLoadingEx(cmd, false));
      //const ok = await dialog.showPostInfo({ postId: activePost.postId ?? "" });
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }

  const onClickConvertToTask = async (cmd: PostCommands) => {
    try {
      setIsLoadingEx(cmd, true);
      dialog.showPostInfo({ postId: activePost.postId ?? "", isConvert2TaskMode: true })
        .then(rs => setIsLoadingEx(cmd, false))
        .catch(ex => setIsLoadingEx(cmd, false));
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }

  const onClickCreateSubTask = async (cmd: PostCommands) => {
    try {
      // setIsLoadingEx(cmd, true);
      // dialog.showPostInfo({ postId: activePost.postId ?? "", isCreateSubTask: true })
      //   .then(rs => setIsLoadingEx(cmd, false))
      //   .catch(ex => setIsLoadingEx(cmd, false));
      dispatch(setCreateSubTask(activePost.postId));
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }


  const onCommandClickIn = async (sender: any, command: PostCommands) => {
    try {
      // console.log('onCommandClickIn');

      switch (command) {
        case PostCommands.MarkAsUnread:
          onMarkUnread(command);
          break;
        case PostCommands.GoToMainTask:
          await onGoToMainTask();
          break;
        case PostCommands.SharePost:
          onSharePost(command);
          break;
        case PostCommands.Flag:
          onToggleFlag(command);
          break;
        case PostCommands.PostStatus:
          onChangePostStatus(command);
          break;
        case PostCommands.PostInfo:
          onClickPostInfo(command);
          break;
        case PostCommands.DeletePost:
          onDeletePost(command);
          break;
        case PostCommands.ConvertToTask:
          onClickConvertToTask(command);
          break;
        case PostCommands.CreateSubTask:
          onClickCreateSubTask(command);
          break;
        case PostCommands.RecurranceSetting:
          dialog.showTaskRecurance({ postId: activePost.postId! });
          break;
      }
      onCommandClick(sender, command, activePost.postId ?? "");
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }

  const CtrlInline = () => {
    const iconGap = showAsDropdown ? "4px" : "2px";
    return (
      <>
        <Tooltip id="tipPostShare" isOpen={tipPostShareShow} />
        <ul className={"list-inline user-chat-nav text-end mb-0"} style={{ display: "flex", gap: iconGap }}>
          {commands.filter(x => isCommandVisible(x)).map((cmd, index) => {
            const icons = cmdIcons[cmd].split('|');
            const laoding = isLoading.some(x => x === cmd);
            return (
              <li
                key={index}
                className={classnames("list-inline-item li-post-command hand ", {
                  "text-red": isCommandActive(cmd)
                })}
                title={cmdTooltips[cmd]}
                onClick={(e) => laoding ? null : onCommandClickIn(this, cmd)}
                data-tooltip-id={getTooltipId(cmd, false)}
                data-tooltip-content={getTooltipId(cmd, true)}
              >
                {laoding && (<LoadingIconWithText loadingText={""} />)}
                {!laoding && (<i className={icons[0]}>{icons.length > 1 ? icons[1] : ""}</i>)}
              </li>
            )
          })}
        </ul>
      </>
    );
  }

  const CtrlDropdown = () => {
    return (
      <div ref={dropdownRef} className="context_menu_base" style={{ top: "30px", right: "10px", minWidth: "150px" }}>
        {commands.filter(x => isCommandVisible(x)).map((cmd, index) => {
          const icons = cmdIcons[cmd].split('|');
          const laoding = isLoading.some(x => x === cmd);
          return (
            <div
              key={index}
              className={classnames("hand hover mb-1 mt-1 d-flex justify-content-start", {
                "text-red": isCommandActive(cmd)
              })}
              onClick={(e) => laoding ? null : onCommandClickIn(this, cmd)}
            >
              <div style={{ minWidth: "28px", maxWidth: "28px" }}>
                {laoding && (<LoadingIconWithText loadingText={""} />)}
                {!laoding && (<i className={icons[0]}>{icons.length > 1 ? icons[1] : ""}</i>)}
              </div>
              <span>{cmdTooltips[cmd]}</span>
            </div>
          )
        })}
      </div>
    );
  }

  return showInLine ? <CtrlInline /> : <CtrlDropdown />
}

export default PostCommand;